import "./styles/ledger.css";
import { mountLayout, type Route } from "./shared/layout";
import { renderDashboard } from "./pages/dashboard";
import { renderPayIns } from "./pages/pay-ins";
import { renderPayOuts } from "./pages/pay-outs";
import { renderSettlement } from "./pages/settlement";
import { renderCrypto } from "./pages/crypto";
import { renderBusiness } from "./pages/business";
import { renderSend } from "./pages/send";
import { renderCards } from "./pages/cards";
import { renderPayroll } from "./pages/payroll";
import { renderBeneficiaries } from "./pages/beneficiaries";
import { renderPromotions } from "./pages/promotions";
import { renderTeam } from "./pages/team";

interface PageContext {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

type PageRenderer = (ctx: PageContext) => void | (() => void);

const pages: Record<Route, PageRenderer> = {
  dashboard: renderDashboard,
  "pay-ins": renderPayIns,
  "pay-outs": renderPayOuts,
  settlement: renderSettlement,
  liquidity: renderSettlement,
  reconciliation: ({ canvas }) => stubPage(canvas, "Reconciliation"),
  statements: ({ canvas }) => stubPage(canvas, "Customer Funds · Statements"),
  crypto: renderCrypto,
  business: renderBusiness,
  send: renderSend,
  cards: renderCards,
  payroll: renderPayroll,
  beneficiaries: renderBeneficiaries,
  "business-statements": ({ canvas }) =>
    stubPage(canvas, "Business · Statements"),
  promotions: renderPromotions,
  team: renderTeam,
};

function stubPage(canvas: HTMLElement, label: string) {
  canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">${label}<span class="page-eyebrow-rule"></span></span>
        <h1 class="page-title">Coming next.</h1>
      </div>
    </section>
    <section class="panel">
      <div class="panel-body" style="padding: 3rem; text-align: center; color: var(--ink-muted);">
        This screen extends the same ledger vocabulary —
        ruled tables, hairline summary cells, stamped-key actions —
        with the data and forms specific to <strong>${label}</strong>.
      </div>
    </section>`;
}

const root = document.getElementById("app");
if (!root) throw new Error("#app not found");

let currentTeardown: (() => void) | null = null;

const validRoutes = new Set<Route>([
  "dashboard",
  "pay-ins",
  "pay-outs",
  "settlement",
  "liquidity",
  "reconciliation",
  "statements",
  "crypto",
  "business",
  "send",
  "cards",
  "payroll",
  "beneficiaries",
  "business-statements",
  "promotions",
  "team",
]);

function routeFromUrl(): Route {
  const path = window.location.pathname.replace(/^\/+|\/+$/g, "");
  if (!path) return "dashboard";
  return validRoutes.has(path as Route) ? (path as Route) : "dashboard";
}

const layout = mountLayout(root, routeFromUrl(), navigate);

function navigate(route: Route, push = true) {
  if (currentTeardown) {
    try {
      currentTeardown();
    } catch {}
    currentTeardown = null;
  }
  if (push) {
    const url = route === "dashboard" ? "/" : `/${route}`;
    window.history.pushState({ route }, "", url);
  }
  layout.setRoute(route);
  const canvas = layout.canvasEl();
  // Reset child-animation delay by re-mounting the canvas div
  canvas.innerHTML = "";
  // Trigger reflow so new children get animated
  void canvas.offsetWidth;

  const fn = pages[route];
  const teardown = fn({ canvas, navigate: (r) => navigate(r) });
  if (typeof teardown === "function") currentTeardown = teardown;
  window.scrollTo({ top: 0, behavior: "instant" });
}

window.addEventListener("popstate", () => {
  navigate(routeFromUrl(), false);
});

navigate(routeFromUrl(), false);
